(ns app.front.ui.v2.dialog
  (:require ["@carbon/icons-react/es/close/24" :default Close24]
            [core.front.portal :as portal]
            [reagent.core :as r]))

(defn- add-listener [el event f]
  (.addEventListener el (name event) f))

(defn- remove-listener [el event f]
  (.removeEventListener el (name event) f))

(def ^:private *prev-overflow (atom nil))

(defn- scroll-lock! []
  (reset! *prev-overflow (.-overflow (.-style js/document.body)))
  (set! (.-overflow (.-style js/document.body)) "hidden"))

(defn- scroll-unlock! []
  (set! (.-overflow (.-style js/document.body)) (or @*prev-overflow ""))
  (reset! *prev-overflow nil))

(defn- header-slot [{:keys [on-close aria-label]} & children]
  [:div.flex.items-start.justify-between.px-6.pt-6.pb-2
   (into [:div.flex-1.min-w-0] children)
   (when on-close
     [:button {:class      "ml-4 p-1 shrink-0 border-0 bg-white"
               :type       "button"
               :aria-label aria-label
               :on-click   on-close}
      [:> Close24]])])

(defn- footer-slot [& children]
  (apply vector :div.px-6.pb-4.pt-2 children))

(defn- activate-dialog! [handler]
  (scroll-lock!)
  (add-listener js/window :keydown handler))

(defn- deactivate-dialog! [handler]
  (scroll-unlock!)
  (remove-listener js/window :keydown handler))

(defn dialog
  "Lightweight modal dialog rendered through a portal. No Carbon dependency.

   Props:
     :open?        — boolean, controls visibility
     :on-close     — fn, called on overlay click or ESC key
     :class        — string, additional Tailwind classes for the card
     :aria-label   — string, accessible label for the dialog
     :close-label  — string, aria-label for the header close button (defaults to 'Close')
     :header       — optional hiccup element(s) rendered as the title area
     :body         — optional hiccup element(s) for the body content
     :footer       — optional hiccup element(s) for the footer content
     :data-cy      — string, test hook

   Missing slots are simply skipped, so a minimal dialog can be header-only,
   footer-only, or even just :body with no chrome."

  [{:keys [open? on-close class aria-label close-label header body footer data-cy]}]
  (r/with-let [*open-state (r/atom false)
               handler     (fn [e]
                             (when (and on-close (= "Escape" (.-key e)))
                               (on-close)))]
    ;; Sync external open? to internal state and manage side effects, including
    ;; when the dialog is mounted with open? already true.
    (when (not= @*open-state open?)
      (if open?
        (activate-dialog! handler)
        (deactivate-dialog! handler))
      (reset! *open-state open?))
    (when open?
      [portal/render-in-portal
       [:div.fixed.inset-0.flex.items-center.justify-center.p-4
        {:data-cy    data-cy
         :role       "dialog"
         :aria-label aria-label
         :aria-modal "true"
         :style      {:z-index 9000}}
        ;; Backdrop
        [:div.absolute.inset-0
         {:style    {:background "rgba(0,0,0,0.5)"}
          :on-click #(when on-close
                       (on-close))}]
        ;; Modal card
        [:div.relative.bg-white.shadow-2xl.max-w-2xl.w-full.max-h-full.overflow-y-auto
         {:class class}
         (when header [header-slot {:on-close   on-close
                                    :aria-label (or close-label "Close")}
                       header])
         body
         (when footer [footer-slot footer])]]
       "modal-container"])
    (finally
      (when @*open-state
        (deactivate-dialog! handler)))))
